// ============================================
// AvatarPickerModal — choose one of Aluvo's curated avatars
// The library is admin-managed (GET /profile-avatars); nothing is hardcoded here.
// Locked avatars (achievement / seasonal) stay visible with their requirement
// but can't be chosen; the server enforces this too.
// ============================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../Modal/Modal';
import { getAvatarLibrary } from '../../services/authService';
import styles from './AvatarPicker.module.css';

const ALL = 'ALL';

export function lockedMessage(avatar) {
    if (!avatar?.locked) return null;
    if (avatar.expired) return `${avatar.requirement} — this season has ended.`;
    const progress = avatar.progress ? ` (${avatar.progress.current}/${avatar.progress.target})` : '';
    return `${avatar.requirement || 'Locked'}${progress}`;
}

function AvatarPickerModal({ currentAvatarId = null, onCancel, onSave, onLockedTap }) {
    const [library, setLibrary] = useState(null); // { avatars, categories }
    const [error, setError] = useState(null);
    const [category, setCategory] = useState(ALL);
    const [selectedId, setSelectedId] = useState(currentAvatarId);
    const [saving, setSaving] = useState(false);
    const [lockedInfo, setLockedInfo] = useState(null); // the locked avatar last tapped

    const load = useCallback(async () => {
        setError(null);
        try {
            setLibrary(await getAvatarLibrary());
        } catch (err) {
            setError(err.message || 'Could not load avatars.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const avatars = useMemo(() => library?.avatars ?? [], [library]);
    // Only offer tabs for categories that currently have avatars.
    const tabs = useMemo(() => {
        const used = new Set(avatars.map((a) => a.category));
        return [{ value: ALL, label: 'All' }, ...(library?.categories ?? []).filter((c) => used.has(c.value))];
    }, [avatars, library]);
    // One section per category that has avatars, in the library's category order.
    const sections = useMemo(() => (library?.categories ?? [])
        .map((c) => ({ ...c, avatars: avatars.filter((a) => a.category === c.value) }))
        .filter((s) => s.avatars.length > 0), [avatars, library]);
    const visibleSections = category === ALL ? sections : sections.filter((s) => s.value === category);
    const selected = avatars.find((a) => a.id === selectedId && !a.locked) ?? null;

    const handlePick = (avatar) => {
        if (avatar.locked) {
            setLockedInfo(avatar);
            onLockedTap?.(avatar);
            return;
        }
        setLockedInfo(null);
        setSelectedId(avatar.id);
    };

    const handleClose = useCallback(() => {
        if (!saving) onCancel();
    }, [saving, onCancel]);

    const handleSave = async () => {
        if (!selected || saving) return;
        setSaving(true);
        try {
            await onSave(selected);
        } catch (err) {
            alert(err.message || 'Could not update your avatar.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen onClose={handleClose}>
            <div className={styles.content}>
                <h3 className={styles.title}>Choose an avatar</h3>

                {tabs.length > 2 && (
                    <div className={styles.tabs} role="tablist" aria-label="Avatar categories">
                        {tabs.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                role="tab"
                                aria-selected={category === t.value}
                                className={`${styles.tab} ${category === t.value ? styles.tabActive : ''}`}
                                onClick={() => setCategory(t.value)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}

                {error ? (
                    <div className={styles.empty}>
                        <p>{error}</p>
                        <button type="button" className={styles.linkBtn} onClick={load}>Try again</button>
                    </div>
                ) : !library ? (
                    <div className={styles.empty}>Loading avatars…</div>
                ) : visibleSections.length === 0 ? (
                    <div className={styles.empty}>No avatars here yet.</div>
                ) : (
                    <div className={styles.sections}>
                        {visibleSections.map((section) => (
                            <section key={section.value} className={styles.section} aria-label={section.label}>
                                {category === ALL && (
                                    <h4 className={styles.sectionTitle}>{section.label}</h4>
                                )}
                                <div className={styles.grid} role="listbox" aria-label={`${section.label} avatars`}>
                                    {section.avatars.map((a) => {
                                        const isSelected = a.id === selectedId && !a.locked;
                                        return (
                                            <button
                                                key={a.id}
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                                aria-disabled={a.locked || undefined}
                                                title={a.locked ? lockedMessage(a) : undefined}
                                                className={`${styles.card} ${isSelected ? styles.cardSelected : ''} ${a.locked ? styles.cardLocked : ''}`}
                                                onClick={() => handlePick(a)}
                                            >
                                                <span className={styles.cardImageWrap}>
                                                    <img src={a.imageUrl} alt={a.altText || a.name} className={styles.cardImage} loading="lazy" />
                                                    {isSelected && <span className={styles.check} aria-hidden="true">✓</span>}
                                                    {a.locked && <span className={styles.lock} aria-label="Locked">🔒</span>}
                                                </span>
                                                <span className={styles.cardName}>{a.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                {lockedInfo && (
                    <div className={styles.lockedInfo} role="status">
                        <strong>🔒 {lockedInfo.name}</strong>
                        <span>{lockedMessage(lockedInfo)}</span>
                    </div>
                )}

                <div className={styles.actions}>
                    <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={saving || !selected || selected.id === currentAvatarId}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default AvatarPickerModal;
